import type {CSSProperties} from 'react';
import {radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';
import {wedgeFormFieldStyle} from './wedgeFormField.js';

export type WedgeLeadsLook = 'lead-close' | 'lead-row' | 'lead-row-added';
export type WedgeLeadsFieldVariant = 'wedge-lead-field' | 'wedge-lead-value';
export type WedgeLeadsSelectVariant = 'wedge-lead-stage';

const LEADS_LOOKS = new Set<string>(['lead-close', 'lead-row', 'lead-row-added']);
export const isWedgeLeadsLook = (look: string): look is WedgeLeadsLook => LEADS_LOOKS.has(look);
export const isWedgeLeadsFieldVariant = (variant: unknown): variant is WedgeLeadsFieldVariant => variant === 'wedge-lead-field' || variant === 'wedge-lead-value';
export const isWedgeLeadsSelectVariant = (variant: unknown): variant is WedgeLeadsSelectVariant => variant === 'wedge-lead-stage';

/* Released Pipeline add forms (prep-leads ManualForm and SavedPicker), off-scale. The five text fields are the sheet field look plus the
   44px minimum height; the value field has no family and no outline of its own, so the browser face and focus ring stay, and the stage
   select has no box sizing. All three are 44px tall at least with a 9px radius and 10px by 12px padding. The Close button of the empty
   saved picker is a full width, 44px tall box (10px radius, 12px padding, 10px of top margin) in the secondary ink on the surface fill.
   A saved row is a full width, left aligned flex row (10px gap, 10px by 8px padding, no fill, no border but a hairline under it); once
   added it takes the default cursor and 0.9 opacity, and the caller passes the accent tint as its fill.
   The raw buttons set no font family: the class of the sheet gives them the body face, so no look sets one.
   No look draws a transparent target (wedgeLeadsDrawsHit): the Close button is 44px tall already, and a saved row is 52px tall or more
   with its neighbors touching, so a region could only repeat the box or overlap the row above and below. */
export const wedgeLeadsDrawsHit = (look: string): boolean => !look.startsWith('lead-');

export function wedgeLeadsStyle(look: WedgeLeadsLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'lead-close': return {
      marginTop: 10, width: '100%', minHeight: 44, padding: 12, borderRadius: radius.md, background: r('surface'),
      border: `1px solid ${r('cardBorder')}`, color: r('labelSec'), fontWeight: weight.semibold, fontSize: 14, cursor: 'pointer',
    };
    case 'lead-row':
    case 'lead-row-added': {
      const added = look === 'lead-row-added';
      return {
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 8px', background: 'none', border: 0,
        borderBottom: `1px solid ${r('separator')}`, cursor: added ? 'default' : 'pointer', opacity: added ? 0.9 : 1,
      };
    }
  }
}

export function wedgeLeadsFieldStyle(variant: WedgeLeadsFieldVariant): CSSProperties {
  const r = wedgeExactRole;
  if (variant === 'wedge-lead-field') return {...wedgeFormFieldStyle('wedge-sheet-field'), minHeight: 44};
  return {
    width: '100%', boxSizing: 'border-box', minHeight: 44, padding: '10px 12px', borderRadius: 9, background: r('surface'), color: r('label'),
    border: `1px solid ${r('cardBorder')}`, fontSize: 14,
  };
}

export function wedgeLeadsSelectStyle(_variant: WedgeLeadsSelectVariant): CSSProperties {
  const r = wedgeExactRole;
  return {
    width: '100%', minHeight: 44, padding: '10px 12px', borderRadius: 9, background: r('surface'), color: r('label'),
    border: `1px solid ${r('cardBorder')}`, fontSize: 14,
  };
}
